"use client";

import { useState, type ReactNode } from "react";
import { Coins, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/auth/AuthProvider";
import { TOKEN_PACKS } from "@/lib/billing/tokenPacks";
import { startTokenCheckout } from "@/lib/billing/startTokenCheckout";
import { CheckoutProgressBar } from "@/components/pricing/CheckoutProgressBar";
import { useLocalizedPricing } from "@/lib/billing/useLocalizedPricing";
import { formatMoney } from "@/lib/billing/money";

const DEFAULT_PACK =
  TOKEN_PACKS.find((pack) => pack.featured)?.variantId ?? TOKEN_PACKS[0].variantId;

// In-app token-pack picker → direct Shopify checkout. Two ways to use it:
//   1. Trigger mode (wrap a button): <TokenPackDialog><Button>…</Button></TokenPackDialog>
//   2. Controlled mode (render as a SIBLING, e.g. from inside a popover so it
//      doesn't unmount when the popover closes):
//        <TokenPackDialog open={open} onOpenChange={setOpen} />
export function TokenPackDialog({
  children,
  open,
  onOpenChange,
}: {
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const { user } = useAuth();
  const [selected, setSelected] = useState<string>(DEFAULT_PACK);
  const [pending, setPending] = useState(false);
  const selectedPack = TOKEN_PACKS.find((pack) => pack.variantId === selected) ?? TOKEN_PACKS[0];
  const { pricing } = useLocalizedPricing();

  // Localized price if Shopify returned one for this variant; else the hardcoded
  // USD display string. (The charged amount is set by the cart, not this label.)
  const priceFor = (variantId: string, fallback: string) =>
    formatMoney(pricing?.tokenPacks[variantId]) ?? fallback;

  function buy() {
    setPending(true);
    // Forward the country we showed localized prices for so the cart currency
    // matches. If none (geo unknown / pricing unavailable), the cart is USD too.
    void startTokenCheckout(selected, user?.email ?? undefined, pricing?.countryCode ?? undefined).then(
      (started) => {
        // On success the page navigates away; keep the loading state until then.
        if (!started) setPending(false);
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      <DialogContent className="sm:max-w-lg">
        <CheckoutProgressBar active={pending} />
        <DialogHeader>
          <DialogTitle>Buy AI tokens</DialogTitle>
          <DialogDescription>
            Token packs never expire and stack on top of your monthly grant. Checkout is on Shopify.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-1 grid gap-2.5 sm:grid-cols-2" role="radiogroup" aria-label="Token packs">
          {TOKEN_PACKS.map((pack) => {
            const isSelected = pack.variantId === selected;
            return (
              <button
                key={pack.variantId}
                type="button"
                role="radio"
                aria-checked={isSelected}
                disabled={pending}
                onClick={() => setSelected(pack.variantId)}
                className={cn(
                  "flex flex-col items-start rounded-2xl border p-4 text-left transition disabled:opacity-60",
                  isSelected
                    ? "border-primary bg-primary/[0.08] ring-2 ring-primary/35"
                    : "border-border/60 bg-foreground/[0.03] hover:border-border",
                )}
              >
                <div className="flex w-full items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">{pack.name}</span>
                  {pack.featured ? (
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                      Best value
                    </span>
                  ) : null}
                </div>
                <span className="mt-1.5 flex items-baseline gap-1.5">
                  <Coins className="h-4 w-4 self-center text-primary" />
                  <span className="text-2xl font-semibold tracking-[-0.03em]">
                    {pack.tokens.toLocaleString()}
                  </span>
                  <span className="text-sm text-muted-foreground">tokens</span>
                </span>
                <span className="mt-1 text-sm font-medium text-foreground">
                  {priceFor(pack.variantId, pack.price)}
                </span>
              </button>
            );
          })}
        </div>

        <p className="mt-2 text-[11px] leading-4 text-muted-foreground">Taxes may apply at checkout.</p>

        <Button onClick={buy} disabled={pending} className="mt-1 w-full rounded-xl">
          {pending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Starting checkout…
            </>
          ) : (
            <>
              Buy {selectedPack.name} — {priceFor(selectedPack.variantId, selectedPack.price)}
            </>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
